import * as Metadata from "../../entities/Metadata";
import { FormatDate, formatValue, notEmpty, toDate, toDateTime } from "../utils/common";
import { EntityType, IRefInfo, IProcessStageInfo, ServerEntityType } from "../../entities/common";
import { NumberFilterValue } from './filters/NumberFilter';
import { DateFilterValue, convertToDate } from './filters/DateFilter';
import { IDropdownOption } from "office-ui-fabric-react";
import { getMultilineText } from "./inputs/RichTextInput";

export class FieldsService {
    public static getFieldDisplayValues(field: Metadata.Field, value: any) {
        switch (field.type) {
            case Metadata.FieldType.Portfolio:
            case Metadata.FieldType.Program:
            case Metadata.FieldType.Project:
            case Metadata.FieldType.Challenge:
            case Metadata.FieldType.Idea:
            case Metadata.FieldType.Objective: {
                return Array.isArray(value)
                    ? (value as { id: string, name: string }[]).map(_ => _.name)
                    : [];
            }
            case Metadata.FieldType.Ref: {
                return Array.isArray(value)
                    ? (value as { id: string, name: string }[]).map(_ => _.name)
                    : [value.name];
            }
            case Metadata.FieldType.Assignment:
            case Metadata.FieldType.Resource:
            case Metadata.FieldType.User: {
                return Array.isArray(value)
                    ? value.map((_: any) => _.fullName)
                    : [];
            }
            case Metadata.FieldType.Text:
            case Metadata.FieldType.Integer:
            case Metadata.FieldType.Decimal:
            case Metadata.FieldType.ProcessStage:
                {
                    const settings = field.settings;
                    if (settings && (FieldsService.isDropDown(field) || FieldsService.isSlider(field, true)) && Array.isArray(value)) {
                        return value.map((_: any) => Metadata.getOption(field, _?.toString())?.text);
                    }

                    if (field.type === Metadata.FieldType.Text || field.type === Metadata.FieldType.ProcessStage) {
                        return value;
                    }

                    if (field.type === Metadata.FieldType.Integer || field.type === Metadata.FieldType.Decimal) {
                        const val = value as NumberFilterValue;
                        const values = [];
                        val.from && values.push(formatValue(val.from, field.settings?.format));
                        val.to && values.push(formatValue(val.to, field.settings?.format));
                        return values;
                    }
                    break;
                }
            case Metadata.FieldType.TimephasedValue:
                {
                    const val = value as NumberFilterValue;
                    const values: string[] = [];
                    val.from !== undefined && values.push(formatValue(val.from, field.settings?.format));
                    val.to !== undefined && values.push(formatValue(val.to, field.settings?.format));
                    return values;
                }
            case Metadata.FieldType.Date:
            case Metadata.FieldType.DateTime:
            case Metadata.FieldType.UniversalDateTime:
                {
                    const val = value as DateFilterValue;
                    const values = [];
                    val.from && values.push(FormatDate(convertToDate(val.from)));
                    val.to && values.push(FormatDate(convertToDate(val.to)));
                    return values;
                }
            case Metadata.FieldType.Flag: {
                return value ? "Yes" : "No";
            }
            case Metadata.FieldType.Hyperlink: {
                return value;
            }
            case Metadata.FieldType.Layout: {
                return value.name;
            }
            default:
        }
        return [];
    }

    public static compareFieldValues(field: Metadata.Field, value: any, attrValue: any) {
        switch (field.type) {
            case Metadata.FieldType.Portfolio:
            case Metadata.FieldType.Program:
            case Metadata.FieldType.Project:
            case Metadata.FieldType.Challenge:
            case Metadata.FieldType.Idea:
            case Metadata.FieldType.Assignment:
            case Metadata.FieldType.Resource:
            case Metadata.FieldType.Objective:
            case Metadata.FieldType.User: {
                if (!Array.isArray(attrValue)) {
                    return false;
                }

                if (Array.isArray(value)) {
                    const entityInfos: { id: string }[] | undefined = value;
                    return entityInfos && attrValue.some(_ => entityInfos.find(__ => __.id === _.id) !== undefined);
                }

                const entityInfo: { id: string } | undefined = value;
                return entityInfo && attrValue.find(_ => _.id === entityInfo.id) !== undefined;
            }
            case Metadata.FieldType.Ref: {
                if (!Array.isArray(attrValue)) {
                    return value && attrValue && 
                        (attrValue.entityType === undefined
                        ? attrValue.entityType === EntityType[ServerEntityType[value.entityType]] && value.name.toLowerCase().indexOf(attrValue.name.toLowerCase()) !== -1
                        : attrValue.id === value.id && attrValue.entityType === EntityType[ServerEntityType[value.entityType]] && attrValue.name === value.name);
                }

                return value && attrValue.find(_ => _.id === value.id && _.entityType === EntityType[ServerEntityType[value.entityType]] && _.name === value.name) !== undefined;
            }
            case Metadata.FieldType.Text:
            case Metadata.FieldType.Integer:
            case Metadata.FieldType.Decimal: {
                if (field.type === Metadata.FieldType.Text && FieldsService.isTag(field)) {
                    return value !== undefined && attrValue.some((_: any) => value.find((__: any) => __ == _) !== undefined);
                }
                if (Array.isArray(attrValue)) {
                    if (Array.isArray(value)) {
                        return attrValue.some(_ => value.find((__: any) => __ == _) !== undefined);
                    }

                    if (value === undefined && field.defaultValue !== undefined) {
                        value = field.defaultValue;
                    }

                    return value !== undefined && attrValue.find(_ => _ == value) !== undefined;
                }
                if (field.type === Metadata.FieldType.Text) {
                    const multiline = getMultilineText(value);
                    return !attrValue || multiline && multiline.toLowerCase().indexOf(attrValue.toLowerCase()) !== -1;
                }
                if (field.type === Metadata.FieldType.Integer || field.type === Metadata.FieldType.Decimal) {
                    const aVal = attrValue as NumberFilterValue;
                    return (aVal.from === undefined || (value !== undefined && aVal.from <= value)) && (aVal.to === undefined || (value !== undefined && value <= aVal.to));
                }
                break;
            }
            case Metadata.FieldType.Date:
            case Metadata.FieldType.UniversalDateTime: {
                const aVal = attrValue as DateFilterValue;
                const date = value && toDate(value);
                const aValFrom = convertToDate(aVal.from);
                const aValTo = convertToDate(aVal.to);

                return (aValFrom === undefined || (date && aValFrom.getBeginOfDay() <= date))
                    && (aValTo === undefined || (date && date <= aValTo.getEndOfDay()));
            }
            case Metadata.FieldType.DateTime: {
                const aVal = attrValue as DateFilterValue;
                const date = value && toDateTime(value);
                const aValFrom = convertToDate(aVal.from);
                const aValTo = convertToDate(aVal.to);

                return (aValFrom === undefined || (date && aValFrom.getBeginOfDay() <= date))
                    && (aValTo === undefined || (date && date <= aValTo.getEndOfDay()));
            }
            case Metadata.FieldType.Flag: {
                return !!value == !!attrValue;
            }
            case Metadata.FieldType.Group: {
                return !value && !attrValue || value && attrValue !== undefined && value.name.toLowerCase().indexOf(attrValue.toLowerCase()) !== -1;
            }
            case Metadata.FieldType.Hyperlink: {
                return !attrValue || value && (value.text?.toLowerCase().includes(attrValue.toLowerCase())
                    || value.url.toLowerCase().includes(attrValue.toLowerCase()));
            }
            case Metadata.FieldType.ProcessStage: {
                const stageInfo = value as IProcessStageInfo;
                const stageNames = attrValue as string[];
                return !stageNames || stageNames.map(_ => _.toLowerCase()).includes(stageInfo?.name.toLowerCase());
            }
            case Metadata.FieldType.TimephasedValue: {
                const aVal = attrValue as NumberFilterValue;
                const resolved = value !== undefined
                    ? Number(value)
                    : undefined;

                return (aVal.from === undefined || (resolved !== undefined && aVal.from <= resolved))
                    && (aVal.to === undefined || (resolved !== undefined && resolved <= aVal.to));
            }
            default:
        }

        return false;
    }

    public static isDropDown(field: Metadata.Field) {
        return (field.type === Metadata.FieldType.Integer || field.type === Metadata.FieldType.Text) &&
            field.settings !== undefined &&
            (field.settings.editControl === "Dropdown" ||
                field.settings.editControl === "ColorStatusDropdown" ||
                field.settings.editControl === "ColorStageDropdown" ||
                field.settings.editControl === "ToggleOption");
    }

    public static isSlider(field: Metadata.Field, withOptions?: boolean) {
        return (field.type === Metadata.FieldType.Integer || field.type === Metadata.FieldType.Decimal)
            && field.settings?.editControl === (withOptions ? "EnumSlider" : "Slider");
    }

    public static isTag(field: Metadata.Field) {
        return field.type === Metadata.FieldType.Text && field.settings?.editControl === "Tag";
    }

    public static isMultiline(field: Metadata.Field): boolean {
        return field.type === Metadata.FieldType.Text && !!field.settings?.multiline;
    }

    public static isIdentifier(field: Metadata.Field) {
        return field.type === Metadata.FieldType.Text && field.settings?.index !== undefined;
    }

    public static getAttributeDisplayValues(fields: Metadata.Field[], value: any): string[] {
        let values: string[] = [];
        Object.keys(value).forEach(attrName => {
            const field = fields.find(_ => _.name === attrName);
            if (field) {
                values = values.concat(FieldsService.getFieldDisplayValues(field, value[attrName]));
            }
        });
        return values;
    }

    public static convert(sourceField: Metadata.Field, destinationField: Metadata.Field, value: any): any {

        if (FieldsService.isTag(destinationField)) {
            const result = Array.isArray(value)
                ? value.map(_ => FieldsService.convertToTag(sourceField, _))
                : [FieldsService.convertToTag(sourceField, value)];

            return result.filter(notEmpty);
        }
        if (FieldsService.isDropDown(destinationField)) {
            const options: IDropdownOption[] = Metadata.getOptions(destinationField);
            const multichoice = !!destinationField.settings?.multichoice;

            const result = Array.isArray(value)
                ? value.map((_: any) => FieldsService.validateWithOptions(sourceField, _, options)).filter(notEmpty)
                : [FieldsService.validateWithOptions(sourceField, value, options)].filter(notEmpty);

            return multichoice ? result : result[0];
        }
        return value;
    }

    private static validateWithOptions(sourceField: Metadata.Field, value: any, options: IDropdownOption[]) {
        if (!options || !value)
            return null;
        const valueStr = FieldsService.convertToString(sourceField, value);
        const option = options.find(_ => _.text == valueStr);
        return option?.key;
    }

    private static convertToTag(sourceField: Metadata.Field, value: string): string {
        const valueStr = FieldsService.convertToString(sourceField, value);
        if (valueStr?.length && !valueStr?.startsWith('#')) {
            return `#${valueStr}`;
        }
        return value;
    }

    private static convertToString(sourceField: Metadata.Field, value: any): string | null {
        if (!value)
            return null;
        switch (sourceField.type) {
            case Metadata.FieldType.Assignment: 
            case Metadata.FieldType.Resource: 
            case Metadata.FieldType.User: 
            case Metadata.FieldType.Ref:
            case Metadata.FieldType.Portfolio:
            case Metadata.FieldType.Program:
            case Metadata.FieldType.Project:
            case Metadata.FieldType.Challenge:
            case Metadata.FieldType.Idea:
            case Metadata.FieldType.Objective:
            case Metadata.FieldType.Layout:
            case Metadata.FieldType.ProcessStage:
                return (value as IRefInfo).name;
            default:
                return value.toString();
        }        
    }
}